import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

export interface AxiosClientConfig {
  baseUrl: string;
  debug?: boolean;
  defaultHeaders?: () => Promise<Record<string, string>>;
}

export class AxiosClient {
  private axiosInstance: AxiosInstance;
  private debug: boolean;
  private defaultHeadersProvider?: () => Promise<Record<string, string>>;

  constructor(config: AxiosClientConfig) {
    this.debug = config.debug || false;
    this.defaultHeadersProvider = config.defaultHeaders;

    this.axiosInstance = axios.create({
      baseURL: config.baseUrl,
      withCredentials: false,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        if (this.defaultHeadersProvider) {
          const headers = await this.defaultHeadersProvider();
          for (const [key, value] of Object.entries(headers)) {
            config.headers.set(key, value);
          }
        }
        
        if (this.debug) {
          console.debug(`[AxiosClient] Request: ${config.method?.toUpperCase()} ${config.url}`);
        }
        
        return config;
      },
      (error) => {
        if (this.debug) {
          console.error("[AxiosClient] Request error:", error);
        }
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use(
      (response) => {
        if (this.debug) {
          console.debug(
            `[AxiosClient] Response [${response.status}]:`,
            response.data
          );
        }
        return response;
      },
      (error) => {
        if (this.debug) {
          console.error(
            `[AxiosClient] Response error [${error.response?.status}]:`,
            error.response?.data
          );
        }
        
        const statusCode = error?.response?.status;
        let errorPayload: any = {};

        if (error.response?.data?.message) {
          errorPayload.message = error.response.data.message;
        } else if (error.response?.data?.data?.message) {
          errorPayload.message = error.response.data.data.message;
        } else {
          errorPayload.message = "Erro ao conectar com o servidor";
        }
        
        errorPayload.status = statusCode;
        
        // Handling 401 or 403 errors (token expired or unauthorized)
        // if (statusCode === 401 || statusCode === 403) {
        //   // Implement your auth logout logic here
        //   // store.dispatch({ type: "auth/logout" });
        // }
        
        return Promise.reject(errorPayload);
      }
    );
  }

  async request<T = any>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.request(config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: "GET", url });
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: "POST", url, data });
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: "PUT", url, data });
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: "PATCH", url, data });
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: "DELETE", url });
  }
}
